import cartApi from "@client/content/modules/Cart/api";
import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { goTo } from "@client/lib/navigation";
import { type AppState, type BaseAppState, type EntityRef, Status } from "@client/types";
import { type AnyAction, type Dispatch } from "redux";
import { initAction } from "../actions";
import { type Cart } from "../Cart/types";
import { eventIdSelector } from "../selectors";
import api from "./api";
import { localStateSelector } from "./selectors";
import { type ProductDetail } from "./types";

export enum Actions {
  InitBegin = "PRODUCT_DETAIL_INIT_BEGIN",
  InitFailure = "PRODUCT_DETAIL_INIT_FAILURE",
  InitSuccess = "PRODUCT_DETAIL_INIT_SUCCESS",

  AddToCartBegin = "PRODUCT_DETAIL_ADD_TO_CART_BEGIN",
  AddToCartFailure = "PRODUCT_DETAIL_ADD_TO_CART_FAILURE",
  AddToCartSuccess = "PRODUCT_DETAIL_ADD_TO_CART_SUCCESS",

  SaveReviewBegin = "PRODUCT_DETAIL_SAVE_REVIEW_BEGIN",
  SaveReviewFailure = "PRODUCT_DETAIL_SAVE_REVIEW_FAILURE",
  SaveReviewSuccess = "PRODUCT_DETAIL_SAVE_REVIEW_SUCCESS"
}

export type InitBeginActionData = EntityRef;
export type InitSuccessActionData = ProductDetail.Api.InitResponse | null;
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  ProductDetail.Api.InitResponse | null,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: AppState, dispatch: any, args: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const eventId = eventIdSelector(state);

    const id = state.page.productId;
    if (id) {
      return api.init(apiInfo, { eventId: eventId ?? null, id, reviewSize: 5, portalId: state.portal.id });
    }

    return Promise.resolve({
      status: 200,
      response: {
        status: "success",
        data: null
      }
    });
  }
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type AddToCartBeginActionData = EntityRef & {
  quantity?: number;
  goToCheckout: boolean;
  variantId?: string;
};
export type AddToCartSuccessActionData = Cart.Api.AddResponse;
const _addToCart = apiAction<AppState, AddToCartBeginActionData, Cart.Api.AddResponse, AddToCartSuccessActionData>({
  types: {
    begin: Actions.AddToCartBegin,
    failure: Actions.AddToCartFailure,
    success: [
      Actions.AddToCartSuccess,
      ({ params, res }) => {
        params.goToCheckout && goTo(res.checkoutUrl);
      }
    ]
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: AddToCartBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const localState = localStateSelector(state, args);
    const quantity = args.quantity ?? 1;
    const variantId = args.variantId ?? localState.productVariants[0].id;

    return cartApi
      .add(apiInfo, {
        variantId,
        quantity,
        resetCart: false
      })
      .then((resp) => {
        if (!resp.error) {
          args.goToCheckout && goTo(resp.response!.data.checkoutUrl);
        }
        return resp;
      });
  }
});

export type AddReviewBeginActionData = EntityRef & ProductDetail.View.AddEditProductReview;
export type AddReviewSuccessActionData = ProductDetail.ProductReview;
const _saveReview = apiAction<
  BaseAppState,
  AddReviewBeginActionData,
  ProductDetail.Api.ProductReview,
  AddReviewSuccessActionData
>({
  types: {
    begin: Actions.SaveReviewBegin,
    failure: Actions.SaveReviewFailure,
    success: Actions.SaveReviewSuccess
  },
  action: (state: BaseAppState, dispatch: Dispatch<AnyAction>, args: AddReviewBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const localState = localStateSelector(state, args);
    const review = localState.product!.userReview;

    if (review) {
      return api.updateReview(apiInfo, localState.product!.id, {
        id: review.id,
        isAnonymous: args.isAnonymous,
        rating: args.rating,
        review: args.review
      });
    }

    return api.addReview(apiInfo, localState.product!.id, {
      email: args.email,
      isAnonymous: args.isAnonymous,
      rating: args.rating,
      review: args.review
    });
  }
});

export const saveReview =
  (ref: EntityRef, data: ProductDetail.View.AddEditProductReview) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== "SavingReview") {
      dispatch(_saveReview({ ...ref, ...data }));
    }
  };

export const addToCart =
  (ref: EntityRef, data: ProductDetail.View.AddToCart) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== Status.Saving) {
      dispatch(_addToCart({ ...ref, quantity: data.quantity, variantId: data.variantId, goToCheckout: false }));
    }
  };

export const buyNow =
  (ref: EntityRef, data: ProductDetail.View.AddToCart) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== Status.Saving) {
      dispatch(_addToCart({ ...ref, quantity: data.quantity, variantId: data.variantId, goToCheckout: true }));
    }
  };
